import React from 'react';
import '../styles/ThirdPartyEstimates.css';
import { Zillow, Redfin, Realtor, X, PadSplit } from '../assets/icons';
import { formatterCompact } from '../styles/GlobalStyles';

// Base URL for PadSplit's host-facing co-living market rent tool. A `zip` query
// param pre-fills the lookup for a specific market.
const PADSPLIT_MARKET_INSIGHTS_URL = 'https://www.padsplit.com/hosts/market-insights';

// Standalone pill linking to PadSplit's co-living market-insights page for a given
// zip. Rendered only for leads that went through co-living analysis, so an investor
// can sanity-check our co-living rents against PadSplit's own market data. The
// caller supplies `className` so the pill can inherit the host page's pill styling.
export function PadSplitLink({ zip, className, label = 'Co-living rents' }) {
    // Normalize to a 5-digit zip: leads can carry a ZIP+4 ("30076-1234") or stray
    // whitespace, and PadSplit's market lookup keys on the plain 5-digit zip. Bail
    // out (render nothing) when there's no usable zip rather than link to a broken
    // lookup.
    const zip5 = String(zip ?? '').match(/\d{5}/)?.[0];
    if (!zip5) {
        return null;
    }
    const href = `${PADSPLIT_MARKET_INSIGHTS_URL}?zip=${zip5}`;
    return (
        <a
            className={className || 'third-party-padsplit-link'}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            // Stop the click bubbling to a clickable parent (a deal card wrapped in a
            // link, or a table row with an onClick). The pill opens PadSplit in a new
            // tab; it must not also trigger the card/row's own navigation.
            onClick={(e) => e.stopPropagation()}
            aria-label={`View PadSplit co-living rents for ${zip5}`}
        >
            <PadSplit width={18} height={18} />
            <span className="body-bold colour-primary">{label}</span>
        </a>
    );
}

function ThirdPartyEstimates(props) {
    const propertyEstimates = props.propertyEstimates;
    const innerWidth = props.innerWidth;
    const small = props.small;
    // Optional external link to the deal's original post (e.g. an X/Twitter post).
    // When provided, an X-logo pill is appended to the estimate stack.
    const postUrl = props.postUrl;
    const onPostClick = props.onPostClick;

    // Hide component if innerWidth is less than 1330px
    if (innerWidth === null || innerWidth === undefined || innerWidth < 1330) {
        return null;
    }

    const viewLink = (url) => {
        if (url && url !== null && url !== undefined) {
            window.open(url, "_blank");
        }
    };

    // Extract estimates and filter out null/undefined values
    const estimates = [];
    
    if (propertyEstimates?.zillow?.estimate !== null && propertyEstimates?.zillow?.estimate !== undefined) {
        estimates.push({
            name: 'zillow',
            estimate: propertyEstimates.zillow.estimate,
            url: propertyEstimates.zillow?.url || null,
            icon: <Zillow />
        });
    }
    
    if (propertyEstimates?.realtor?.estimate !== null && propertyEstimates?.realtor?.estimate !== undefined) {
        estimates.push({
            name: 'realtor',
            estimate: propertyEstimates.realtor.estimate,
            url: propertyEstimates.realtor?.url || null,
            icon: <Realtor />
        });
    }
    
    if (propertyEstimates?.redfin?.estimate !== null && propertyEstimates?.redfin?.estimate !== undefined) {
        estimates.push({
            name: 'redfin',
            estimate: propertyEstimates.redfin.estimate,
            url: propertyEstimates.redfin?.url || null,
            icon: <Redfin />
        });
    }

    // Append the original-post link (X logo) when provided.
    if (postUrl) {
        estimates.push({
            name: 'x',
            label: 'View on X',
            url: postUrl,
            icon: <X className="third-party-estimates-x-icon" />,
            isPost: true,
        });
    }

    // Don't render if there's nothing to show
    if (estimates.length === 0) {
        return null;
    }

    return (
        <div className={`third-party-estimates-container ${small ? 'third-party-estimates-container-small' : ''}`}>
            {estimates.map((item, index) => (
                <div key={index} className="third-party-estimates-item">
                    <div
                        className="third-party-estimates-logo-container bg-colour-white"
                        onClick={() => {
                            if (item.isPost && onPostClick) onPostClick();
                            viewLink(item.url);
                        }}
                    >
                        {item.icon}
                    </div>
                    <span className={`${small ? 'label-bold' : 'body-bold'} colour-primary`}>
                        {item.isPost ? item.label : formatterCompact.format(item.estimate)}
                    </span>
                </div>
            ))}
        </div>
    );
}

export default ThirdPartyEstimates;

